import {
  Box,
  Typography,
  IconButton,
  Grid,
  Chip,
  Tooltip,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Grading as GradingIcon,
  Devices as DevicesIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import type { Application } from '../../../types/application';
import STATUS_COLORS from '../../../../../constants/statusColors';

interface Props {
  application: Application;
  hasChanges: boolean;
  loading: boolean;
  onClose: () => void;
  onCopyAccount: () => void;
  onCopyAddress: () => void;
}

const OrderDetailHeader = ({ 
  application, 
  hasChanges, 
  loading, 
  onClose, 
  onCopyAccount,
  onCopyAddress 
}: Props) => {
  return (
    <Box sx={{
      p: 3,
      borderBottom: '1px solid #e0e7ef',
      bgcolor: '#fff',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      borderRadius: '16px 0 0 0'
    }}>
      {/* 상단: 제목과 닫기 버튼 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color="#1976d2" sx={{ display: 'flex', alignItems: 'center' }}>
          <EditIcon sx={{ mr: 1 }} />
          신청서 상세 편집
          {hasChanges && <Badge color="warning" variant="dot" sx={{ ml: 1 }} />}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 🎯 핵심 정보 카드 */}
      <Box sx={{ 
        bgcolor: '#f8fafc', 
        borderRadius: 2,
        border: '1px solid #e0e7ef'
      }}>
        {/* 상단: 기본 정보 */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e7ef' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Box display="flex" alignItems="center">
                <PersonIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#1976d2">
                    {application.user_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {application.phone_number}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center">
                <GradingIcon sx={{ mr: 1, fontSize: 18, color: STATUS_COLORS[application.status] || '#757575' }} />
                <Chip
                  label={application.status}
                  size="small"
                  sx={{
                    bgcolor: STATUS_COLORS[application.status] || '#757575',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center">
                <DevicesIcon sx={{ mr: 1, fontSize: 18, color: '#666' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {application.devices?.length || 0}개 기기
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 중단: 계좌 정보 */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e7ef' }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <Box display="flex" alignItems="center">
                <AccountBalanceIcon sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                    은행 • 예금주
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="#1976d2" noWrap>
                    {application.bank_name || '정보없음'} • {application.account_holder || application.bank_owner || '정보없음'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={5}>
              <Box display="flex" alignItems="center">
                <CreditCardIcon sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                    계좌번호
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="#1976d2"
                    sx={{ fontFamily: 'monospace', letterSpacing: '0.5px', fontSize: '0.85rem' }}
                    noWrap
                  >
                    {application.bank_account || '정보없음'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={2}>
              {application.bank_account && (
                <Tooltip title="계좌번호 복사" arrow>
                  <IconButton size="small" onClick={onCopyAccount} sx={{ color: '#1976d2' }}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* 하단: 수거 주소 */}
        {application.pickup_address && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={10}>
                <Box display="flex" alignItems="center">
                  <LocationOnIcon sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                      수거 주소
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1976d2"
                      sx={{ fontSize: '0.85rem' }}
                      noWrap
                    >
                      {application.pickup_address}
                      {application.pickup_address_detail && ` ${application.pickup_address_detail}`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Tooltip title="주소 복사" arrow>
                  <IconButton size="small" onClick={onCopyAddress} sx={{ color: '#1976d2' }}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
    </Box>
  );
};

export default OrderDetailHeader; 